import { UserClient } from "@app/api2/api";
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@common/PageHeader";
import { buildUrl, AppRoutes } from "../AppRoutes";
import toast from "react-hot-toast";
import { useUser } from "../Contexts/UserContext";
import { areYouSure2 } from "../AreYouSure";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { replaceInString } from "@common/Utils";
import { BPContainer } from "@/components/bp/bpcontainer";

interface UserDetailsProps {
    userId: string;
    canCreateUser: boolean;
}

export const UserDetails: React.FC<UserDetailsProps> = ({ userId, canCreateUser }) => {
    const client = new UserClient();
    const me = useUser();
    const [uid, setUid] = React.useState<string | null>(userId == "me" ? me.user?.details?.id : userId);
    const [changePasswordOpen, setChangePasswordOpen] = React.useState(false);
    // const [isSelf, setIsSelf] = React.useState<boolean>(false);
    const isSelf = userId == "me";



    React.useEffect(() => {
        setUid(userId == "me" ? me.user?.details?.id : userId);
        // setIsSelf(uid === me.user?.details?.id);
    }, [userId, me.user?.details?.id]);


    const query = useQuery({
        queryKey: ['userDetails', userId],
        queryFn: async () => {
            const response = await client.get(uid);
            return { response };
        },
        enabled: me.user.details != null, // Only run the query if userId is provided
    });

    if (!uid) {
        return <p>Loading...</p>;
    }


    const userModel = query.data?.response;

    return (
        <BPContainer>
            <PageHeader header={replaceInString(window.translation.userView.details, { '{userName}': userModel?.name, '{email}': userModel?.email })}

                buttons={[
                    ...(!isSelf && me?.user?.canCreateUser ? [
                        { label: window.translation.common.edit, to: buildUrl(AppRoutes.UserEdit, { userId }) },
                        {
                            label: window.translation.userView.resendEmail, action: async () => {
                                await client.resendEmail(uid);
                                toast.success(window.translation.userView.emailResent, { position: 'top-center' });
                            }
                        }
                    ] : []),
                    ...(!isSelf && me?.user?.canCreateUser ? [{
                        label: userModel?.active ? window.translation.common.disable : window.translation.common.enable,
                        negative: userModel?.active,
                        action: async () => {
                            if (userModel?.active) {
                                areYouSure2({
                                    message: window.translation.userView.disableConfirm,
                                    title: window.translation.common.confirmDisable,
                                    action: async () => {
                                        await client.disable(uid);
                                        await query.refetch();
                                    }
                                });

                            }
                            else if (!userModel?.active) {
                                await client.enable(uid);
                                await query.refetch();
                            }

                        }
                    }] : []),

                    ...(isSelf ? [{
                        label: window.translation.userView.changePassword, action: async () => {
                            // location.assign(me.user?.changePasswordUrl!);
                            setChangePasswordOpen(true);
                        }
                    }] : []),
                    ...(userModel?.twoFactorEnabled ? [{
                        label: 'Disable 2FA',
                        action: async () => {
                            areYouSure2({
                                message: 'Disable two-factor authentication?',
                                title: window.translation.common.confirmDisable,
                                action: async () => {
                                    await client.disable2fa(uid);
                                    await query.refetch();
                                }
                            });
                        },

                    }] : [])


                ]}
            />

            <ChangePasswordDialog
                open={changePasswordOpen}
                onClose={() => setChangePasswordOpen(false)}
            />

            {query.isLoading && <p>Loading...</p>}
            {query.isError && <p>Error fetching user details</p>}
            {userModel && (
                <div>
                    <table className="ui definition table">
                        <thead>
                            <tr>
                                <th></th>
                                <th>{window.translation.common.value}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{window.translation.user.name}</td>
                                <td>{userModel.name}</td>
                            </tr>
                            <tr>
                                <td>{window.translation.common.emailAddress}</td>
                                <td>{userModel.email}</td>
                            </tr>
                            <tr>
                                <td>{window.translation.user.role}</td>
                                <td>{userModel.roles?.join(", ")}</td>
                            </tr>
                            <tr>
                                <td>{window.translation.common.labs}</td>
                                <td>{(userModel.labs ?? []).map(l => l.name).join(", ")}</td>
                            </tr>

                        </tbody>
                    </table>
                </div>
            )}
        </BPContainer>
    );
};
